import React, { useState, useContext } from 'react'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/Components/ui/dialog";
import { Upload } from 'lucide-react';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/Components/ui/tooltip"
import { PageLockContext } from '@/Components/PageLockProvider';
import { formatDate } from 'date-fns';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/Components/ui/select';
import Confirmation from '@/hooks/confirmation';

interface CSV_ImporterProps {
    propertyTypes: { name: string, type: any, example: any }[],
    setNewRows: React.Dispatch<React.SetStateAction<any[]>>,
    bgColor?: string,
    title: string,
}

function CSV_Importer({ propertyTypes, setNewRows, bgColor, title }: CSV_ImporterProps) {
    const lock = useContext(PageLockContext);

    const [dateformat, setDateformat] = useState("yyyy-MM-dd");

    const [dialogTitle, setDialogTitle] = useState(title);
    const [dialogContent, setDialogContent] = useState<JSX.Element | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    const [errorList, setErrorList] = useState<string[]>([]);

    React.useEffect(() => {
        if (!dialogOpen) {
            resetDialog();
        } else {
            // when opening or when date format changes while open, ensure initial content is shown
            setDialogContent(getInitialDialogContent());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dialogOpen, dateformat]);

    function confirmImport(data: any[], errors: string[]) {
        setDialogTitle(`You are about to import ${data.length} rows. Do you want to proceed?`);
        const confirmationContent = (
            <div>
                {errors.length > 0 && (
                    <div className="mb-4 p-2 border border-red-500 bg-red-100 rounded">
                        <p className="font-medium text-red-700 mb-2">The following errors were found during import:</p>
                        <ul className="list-disc list-inside text-red-700">
                            {errors.map((error, index) => (
                                <li key={index}>{error}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        );
        // ensure state reflects parsed errors before showing dialog content
        setErrorList(errors);
        setDialogContent(
            <Confirmation
                onConfirm={() => { setNewRows(data); setDialogOpen(false); }}
                onCancel={() => resetDialog()}
                confirmColour="bg-chart-green"
                content={confirmationContent}
            />
        );
    }

    function formatImportData(header: string, value: string, errors?: string[]) {
        const propType = propertyTypes.find(prop => prop.name === header)?.type;

        switch (propType) {
            case Number:
                if (value === "") return undefined;
                const n = Number(value);
                if (isNaN(n)) {
                    const msg = `Invalid number format for ${header}: ${value} - 0 will be used`;
                    if (errors) errors.push(msg); else setErrorList(prev => [...prev, msg]);
                    return 0;
                }
                return n;
            case Date:
                const date = parseDateByFormat(value, dateformat);
                if (date === undefined || isNaN(date.getTime())) {
                    const msg = `Invalid date format for ${header}: ${value} - current date will be used`;
                    if (errors) errors.push(msg); else setErrorList(prev => [...prev, msg]);
                    return new Date();
                }
                return date;
            case Boolean:
                if (!value) return false;
                switch (value.toLowerCase()) {
                    case "true":
                    case "1":
                    case "yes":
                        return true;
                    case "false":
                    case "0":
                    case "no":
                        return false;
                    default:
                        const msg = `Invalid boolean format for ${header}: ${value} - false will be used`;
                        if (errors) errors.push(msg); else setErrorList(prev => [...prev, msg]);
                        return false;
                }
            default:
                return value;
        }
    }

    function parseDateByFormat(val: string, fmt: string): Date | undefined {
        if (!val) return undefined;
        // remove time component if present
        const datePart = val.includes("T") ? val.split("T")[0] : val.includes(" ") ? val.split(" ")[0] : val;
        const v = datePart;
        const sep = fmt.includes("/") ? "/" : "-";
        const parts = v.split(sep).map(p => p.trim());
        if (parts.length !== 3) {
            // fallback to native parse
            const d = new Date(val);
            return isNaN(d.getTime()) ? undefined : d;
        }

        let year = NaN, month = NaN, day = NaN;
        switch (fmt) {
            case "yyyy-MM-dd":
            case "yyyy/MM/dd":
                year = Number(parts[0]);
                month = Number(parts[1]);
                day = Number(parts[2]);
                break;
            case "MM-dd-yyyy":
            case "MM/dd/yyyy":
                month = Number(parts[0]);
                day = Number(parts[1]);
                year = Number(parts[2]);
                break;
            case "dd-MM-yyyy":
            case "dd/MM/yyyy":
                day = Number(parts[0]);
                month = Number(parts[1]);
                year = Number(parts[2]);
                break;
            default:
                const d = new Date(val);
                return isNaN(d.getTime()) ? undefined : d;
        }

        if ([year, month, day].some(n => isNaN(n))) return undefined;
        return new Date(year, month - 1, day);
    }

    function resetDialog() {
        setDialogTitle(title);
        setDialogContent(getInitialDialogContent());
        setErrorList([]);
        setDateformat("yyyy-MM-dd");
    }

    function getInitialDialogContent() {
        return (
            <div className="flex flex-col">
                <p>Upload a CSV file containing actions.</p>
                <p className="mb-4">The CSV file should have the following columns:</p>
                <table className="table-auto mb-4 w-full text-sm">
                    <thead>
                        <tr className="text-left">
                            <th className="px-2 py-1">Column Name</th>
                            <th className="px-2 py-1">Example</th>
                        </tr>
                    </thead>
                    <tbody>
                        {propertyTypes.map((prop) => {
                            return (
                                <tr key={prop.name} className="even:bg-muted-foreground/5">
                                    <td className="px-2 py-1 align-top">{prop.name}</td>
                                    <td className="px-2 py-1 align-top">{(prop.type === Date && prop.example) ? formatDate(prop.example, dateformat) : prop.example}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <div className='flex flex-row items-center mb-4'>
                    <label className="mb-2 font-medium text-nowrap mr-2">Select Date Format:</label>
                    <Select
                        value={dateformat}
                        onValueChange={(value: string) => setDateformat(value)}
                    >
                        <SelectTrigger className="mb-0 p-2 border border-input rounded-md bg-muted-foreground/5">
                            <SelectValue placeholder="Select a date format" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="yyyy-MM-dd">YYYY-MM-DD</SelectItem>
                            <SelectItem value="MM-dd-yyyy">MM-DD-YYYY</SelectItem>
                            <SelectItem value="dd-MM-yyyy">DD-MM-YYYY</SelectItem>
                            <SelectItem value="yyyy/MM/dd">YYYY/MM/DD</SelectItem>
                            <SelectItem value="MM/dd/yyyy">MM/DD/YYYY</SelectItem>
                            <SelectItem value="dd/MM/yyyy">DD/MM/YYYY</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <input
                        type="file"
                        accept=".csv"
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const reader = new FileReader();
                            reader.onload = (event) => {
                                const csv = event.target?.result;
                                if (typeof csv !== "string") return;
                                // split lines robustly
                                const lines = csv.split(/\r?\n/).filter(line => line.trim() !== "");
                                if (lines.length === 0) return;
                                const headers = lines[0].split(",").map(h => h.trim().replace(/"/g, ""));
                                const localErrors: string[] = [];
                                const data = lines.slice(1).map((line) => {
                                    const values = line.split(",").map(v => v.trim().replace(/"/g, ""));
                                    const obj = headers.reduce((acc, header, index) => {
                                        const raw = values[index] ?? "";
                                        acc[header] = formatImportData(header, raw, localErrors);
                                        return acc;
                                    }, {} as any);
                                    return obj;
                                });
                                // set errors collected during parsing and show confirmation with those errors
                                setErrorList(localErrors);
                                confirmImport(data, localErrors);
                            };
                            reader.readAsText(file);
                        }}
                    />
                </div>
                {errorList.length > 0 && (
                    <div className="mt-4 text-sm text-red-700">
                        <p className="font-medium">Parsing errors:</p>
                        <ul className="list-disc list-inside">
                            {errorList.map((e, i) => <li key={i}>{e}</li>)}
                        </ul>
                    </div>
                )}
            </div>
        );
    }

    if (lock?.viewOnly) {
        return <></>;
    }

    return (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
                <TooltipProvider delayDuration={1}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div id="uploadCSV" className={"flex flex-row p-2 m-2 h-min text-table-header-foreground rounded-full place-content-between " + (bgColor ? bgColor : "bg-table-header")} onClick={() => setDialogOpen(true)}>
                                <Upload className="w-6 h-6" />
                            </div>
                        </TooltipTrigger>
                        <TooltipContent className="flex flex-col bg-background">
                            <div className="flex flex-row text-muted-foreground place-content-between">
                                <span className="p-2">Upload CSV</span>
                            </div>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </DialogTrigger>
            <DialogContent className="flex flex-col max-w-fit">
                <DialogHeader>
                    <DialogTitle>{dialogTitle}</DialogTitle>
                </DialogHeader>
                {dialogContent}
            </DialogContent>
        </Dialog>
    )
}

export default CSV_Importer
